import type { ComponentType } from 'react'

/**
 * PAGE LAYOUT ENGINE — registry générique des widgets.
 *
 * Chaque widget se déclare ICI (bornes, page, permission éventuelle) — pas
 * de if/else dispersés dans React. Le registry est la source de vérité du
 * rendu ; le serveur possède sa propre allowlist (core/layout) pour la
 * validation des écritures. Un widget dont la permission manque n'est
 * JAMAIS rendu, quel que soit le JSON de layout — le layout ne contourne
 * pas le RBAC.
 *
 * Doctrine d'éligibilité (docs/architecture/page-layout-engine.md) :
 * cockpits → libres · hubs → configurables · formulaires/wizards → FIXES.
 */

export type VisibilityMode = 'ALWAYS' | 'BEFORE_START' | 'FROM_START' | 'HIDDEN'

export const VISIBILITY_LABELS: Record<VisibilityMode, string> = {
  ALWAYS: 'Toujours',
  BEFORE_START: 'Avant la soirée',
  FROM_START: 'À partir du lancement',
  HIDDEN: 'Masquée',
}

/** Ce que chaque mode veut dire, en une phrase, dans la modale. */
export const VISIBILITY_HINTS: Record<VisibilityMode, string> = {
  ALWAYS: 'Présente avant, pendant et après la soirée.',
  BEFORE_START: 'Utile pour préparer — disparaît une fois la soirée lancée.',
  FROM_START: 'N’apparaît qu’au lancement, et reste ensuite pour le bilan.',
  HIDDEN: 'Retirée de la page. Elle reste réglable ici.',
}

export interface WidgetPlacement {
  id: string
  x: number
  y: number
  w: number
  h: number
  /** Ancien booléen : lu pour les dispositions antérieures, jamais réécrit. */
  hidden?: boolean
  visibility?: VisibilityMode
  config?: Record<string, unknown>
}

/** Capacités déclarées par le SERVEUR — le front ne les invente pas. */
export interface WidgetChoice { value: string; label: string }

/** Familles de réglages. Les libellés arrivent AVEC les capacités : le
 *  navigateur n'a pas sa propre idée de la façon de grouper. */
export type WidgetOptionGroup = 'OPENING' | 'COMPARISON'

export interface WidgetConstraint {
  when: Record<string, string>
  forbid: Record<string, string>
  reason: string
  message: string
}

export interface WidgetCapabilities {
  defaultView?: { key: string; label: string; hint: string; choices: WidgetChoice[] }
  content?: { key: string; label: string; hint: string }
  options?: {
    key: string; label: string; hint: string; choices: WidgetChoice[]
    group?: WidgetOptionGroup
  }[]
  constraints?: WidgetConstraint[]
}

/**
 * La première contrainte violée — MÊME LECTURE QUE LE SERVEUR.
 *
 * Réimplémentée ici (et non importée : `apps/web` ne dépend pas de
 * `@gestion/core`), mais elle lit les contraintes que le serveur a
 * envoyées. Le navigateur ne connaît donc aucune règle que le serveur
 * ignorerait — il ne fait qu'avertir plus tôt.
 */
export function violatedConstraint(
  cap: WidgetCapabilities | undefined, config: Record<string, unknown>,
): WidgetConstraint | null {
  const eff = effectiveWidgetConfig(cap, config)
  for (const c of cap?.constraints ?? []) {
    if (!Object.entries(c.when).every(([k, v]) => eff[k] === v)) continue
    if (Object.entries(c.forbid).some(([k, v]) => eff[k] === v)) return c
  }
  return null
}

/**
 * La configuration TELLE QUE LA TUILE LA VERRA.
 *
 * Une clé absente n'est pas « pas de valeur » : le contrôle affiche alors le
 * premier choix déclaré. Vérifier les combinaisons sur la configuration
 * brute laissait passer le cas le plus courant — celui du réglage jamais
 * touché. Constaté dans le navigateur avant correction.
 */
export function effectiveWidgetConfig(
  cap: WidgetCapabilities | undefined, config: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...config }
  if (cap?.defaultView && out[cap.defaultView.key] === undefined) {
    out[cap.defaultView.key] = cap.defaultView.choices[0]?.value
  }
  for (const o of cap?.options ?? []) {
    if (out[o.key] === undefined) out[o.key] = o.choices[0]?.value
  }
  return out
}

/** Le mode effectif d'un placement, compatible avec l'ancien booléen. */
export function modeOf(p: WidgetPlacement): VisibilityMode {
  return p.visibility ?? (p.hidden === true ? 'HIDDEN' : 'ALWAYS')
}

/**
 * LA RÈGLE DE VISIBILITÉ — la même que côté serveur, volontairement.
 *
 * Une phase inconnue n'enlève rien : masquer par défaut ferait disparaître
 * des blocs sans que personne l'ait décidé. Une soirée ANNULÉE suit la
 * doctrine déjà en vigueur — elle n'ouvrira pas, on ne montre donc pas ce
 * qui n'a de sens qu'une fois ouverte.
 */
export function visibleForPhase(
  mode: VisibilityMode, phase: string | null | undefined,
): boolean {
  if (mode === 'HIDDEN') return false
  if (mode === 'ALWAYS') return true
  if (!phase) return true
  const avant = phase === 'UPCOMING' || phase === 'CANCELLED'
  return mode === 'BEFORE_START' ? avant : !avant
}

export interface WidgetDef {
  id: string
  label: string
  description: string
  minW: number
  minH: number
  maxH?: number
  defaultW: number
  defaultH: number
  /** Permission SOURCE requise pour rendre le widget (RBAC au rendu). */
  requiredPermission?: string
  /** Pictogramme de la tuile — utilisé par la modale de configuration. */
  Icon?: ComponentType<{ size?: number }>
  /** Le composant reçoit la config du placement (widget-specific). */
  Component: ComponentType<{ config?: Record<string, unknown> }>
}

export interface PageLayoutDef {
  pageKey: string
  widgets: WidgetDef[]
  /** Disposition par défaut (le « preset » de base — d'autres presets
   *  pourront s'ajouter ici sans changer le moteur). */
  defaultLayout: WidgetPlacement[]
}

const pages = new Map<string, PageLayoutDef>()

export function registerPageLayout(def: PageLayoutDef): void {
  pages.set(def.pageKey, def)
}

export function getPageLayout(pageKey: string): PageLayoutDef | null {
  return pages.get(pageKey) ?? null
}

/** Complète un layout sauvegardé : widgets inconnus retirés, widgets
 *  manquants ajoutés (masqués s'ils n'étaient pas dans la sauvegarde —
 *  l'utilisateur les a peut-être retirés volontairement ; les NOUVEAUX
 *  widgets apparaissent en bas, visibles, pour être découvrables). */
export function reconcileLayout(def: PageLayoutDef, saved: WidgetPlacement[] | null): WidgetPlacement[] {
  if (!saved) return def.defaultLayout.map((w) => ({ ...w }))
  const known = new Map(def.widgets.map((w) => [w.id, w]))
  const kept = saved.filter((p) => known.has(p.id)).map((p) => ({ ...p }))
  const present = new Set(kept.map((p) => p.id))
  const maxY = kept.reduce((m, p) => Math.max(m, p.y + p.h), 0)
  let y = maxY
  for (const d of def.defaultLayout) {
    if (present.has(d.id)) continue
    // Une tuile NOUVELLE arrive visible et en bas : découvrable, sans
    // bousculer ce qui était déjà en place.
    kept.push({ ...d, y, visibility: d.visibility ?? 'ALWAYS' })
    y += d.h
  }
  return kept
}
